import React, { useState, useRef, useEffect } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { SUPPORTED_LANGUAGES } from "@/i18n/languages";

const LanguageSwitcher = () => {
  const { lang, changeLang, loading } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === lang) || SUPPORTED_LANGUAGES[0];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="nav-lang-toggle"
        className="flex items-center gap-1.5 text-sm border border-[#E2E8F0] px-3 py-1.5 rounded-full hover:border-[#2563EB] hover:text-[#2563EB] transition-colors"
        aria-label="Choose language"
        aria-expanded={open}
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span className="font-mono text-xs uppercase tracking-widest">{current.code}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        {loading && <span className="w-2 h-2 rounded-full bg-[#2563EB] animate-pulse ml-1" />}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 max-h-[28rem] overflow-y-auto bg-white border border-[#E2E8F0] rounded-xl shadow-lg z-50 py-2"
          data-testid="lang-menu"
        >
          <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-[#94A3B8] flex items-center gap-2">
            <Globe size={12} /> 23 Languages
          </div>
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => { changeLang(l.code); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-[#EFF6FF] text-left transition-colors ${
                l.code === lang ? "text-[#2563EB] bg-[#EFF6FF]/60" : "text-[#0F172A]"
              }`}
              data-testid={`lang-${l.code}`}
            >
              <span className="text-lg leading-none w-6">{l.flag}</span>
              <span className="flex-1">{l.name}</span>
              <span className="font-mono text-xs text-[#94A3B8]">{l.code.toUpperCase()}</span>
              {l.code === lang && <Check size={14} className="text-[#2563EB]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
